import { SEOHead } from "@/components/SEOHead";
import { Layout } from "@/components/layout/Layout";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  CheckCircle,
  AlertTriangle,
  Brain,
  TrendingUp,
  Users,
  Clock,
  Target,
  Activity,
  Eye,
  Compass,
  PlayCircle,
} from "lucide-react";

const DealIntelligence = () => {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "Deal Intelligence: The Deal-Level Layer of Revenue Intelligence",
      description:
        "What deal intelligence is, the six signals that actually predict close, and how to operationalize it without rep input.",
      author: { "@type": "Organization", name: "NoQ5" },
      publisher: { "@type": "Organization", name: "NoQ5", url: "https://noq5.ai" },
      datePublished: "2026-05-17",
      dateModified: "2026-05-17",
      mainEntityOfPage: "https://noq5.ai/deal-intelligence",
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "What is deal intelligence?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Deal intelligence is the deal-level layer of revenue intelligence. It uses captured activity to compute objective signals on each opportunity: stage age, multi-thread coverage, response latency, momentum, sentiment and buying-committee changes. The output tells a manager which deals are real, which are stalled and which are at risk, before the forecast call.",
          },
        },
        {
          "@type": "Question",
          name: "What is the difference between deal intelligence and pipeline intelligence?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Deal intelligence is computed at the single-opportunity level. Pipeline intelligence rolls deal-level signals up to coverage, velocity, conversion bands and risk concentration across the whole pipeline. You need both: deal intelligence to inspect any one opportunity, pipeline intelligence to read the whole book.",
          },
        },
        {
          "@type": "Question",
          name: "What signals does deal intelligence track?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "The six that actually predict close: activity density, multi-thread coverage, response latency, stage age versus cohort, buying-committee stability, and momentum signals (pricing requests, security review, legal involvement, sentiment shifts). Most platforms cover three or four. Modern autonomous CRM covers all six and recomputes them continuously.",
          },
        },
        {
          "@type": "Question",
          name: "How does deal intelligence improve forecast accuracy?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "By moving the forecast from a weighted roll-up of stage and amount to an evidence-based computation. A deal in late stage with two months of stalled activity is downgraded automatically. A deal in mid stage with rapid multi-thread expansion is upgraded. The forecast is no longer at the mercy of rep optimism.",
          },
        },
        {
          "@type": "Question",
          name: "Does deal intelligence require rep input?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "It should not. The whole point is to compute deal health from captured activity (email, calendar, calls, CRM events) so the answer is independent of what reps choose to log. NoQ5 specifically refuses to depend on rep submissions for any deal intelligence signal.",
          },
        },
      ],
    },
  ];

  return (
    <Layout>
      <SEOHead
        title="Deal Intelligence: The Deal-Level Layer of Revenue Intelligence | NoQ5"
        description="What deal intelligence is, the six signals that predict close, and how to operationalize it without rep input. The operator's guide for revenue leaders."
        keywords="deal intelligence, ai deal intelligence, deal scoring, deal health, deal risk scoring, deal momentum, ai deal scoring, opportunity intelligence, deal inspection, pipeline deal intelligence, revenue intelligence deal layer, deal coaching ai, autonomous CRM deal intelligence"
        canonicalUrl="https://noq5.ai/deal-intelligence"
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-background via-background to-primary/5">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center">
            <span className="inline-block px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-semibold mb-6">
              DEAL-LEVEL GUIDE
            </span>
            <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6 leading-tight">
              Deal Intelligence.<br />
              <span className="text-primary">Know Which Deals Are Real.</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-4">
              Forecast accuracy is a deal-by-deal problem masquerading as a pipeline problem. Get deal intelligence right and the forecast fixes itself.
            </p>
            <p className="text-base text-muted-foreground max-w-3xl mx-auto mb-8">
              This is the operator's guide. The six signals that actually predict close, where most platforms stop, and what autonomous CRM does differently.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground" asChild>
                <Link to="/trial?scrollTo=demo">Book a Demo <ArrowRight className="ml-2 h-5 w-5" /></Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/revenue-intelligence">Revenue Intelligence</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Definition */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold mb-6">What Is Deal Intelligence?</h2>
          <p className="text-lg text-muted-foreground mb-4">
            <strong className="text-foreground">Deal intelligence</strong> is the deal-level layer of revenue intelligence. It uses captured activity, email, calendar, calls, CRM events, to compute objective signals on each opportunity. Stage age. Multi-thread coverage. Response latency. Buying-committee changes. Momentum.
          </p>
          <p className="text-lg text-muted-foreground mb-4">
            The output is simple: a manager looks at a deal and sees instantly whether it is real, stalled or at risk, without having to interrogate the rep.
          </p>
          <p className="text-lg text-muted-foreground">
            This is the layer that turns a forecast review from a memory test into a data review.
          </p>
        </div>
      </section>

      {/* The 6 signals */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-4">The 6 Signals That Actually Predict Close</h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            Stage and amount are necessary. They are not sufficient. These are the signals that move forecast accuracy.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: Activity, title: "1. Activity Density", desc: "Meetings, emails and call minutes per week, trended against historical closed-won shapes. Density collapse is the earliest churn signal in any deal." },
              { icon: Users, title: "2. Multi-Thread Coverage", desc: "How many stakeholders on the buyer side are engaged. Single-threaded deals close at a small fraction of multi-threaded ones, regardless of stage." },
              { icon: Clock, title: "3. Response Latency", desc: "Hours between rep send and buyer reply. Latency expanding 2x week-over-week is one of the strongest leading indicators of slip." },
              { icon: Clock, title: "4. Stage Age vs Cohort", desc: "How long the deal has been in this stage relative to historical deals of similar size, segment and motion that closed. Tells you when stage age has tipped from normal to suspicious." },
              { icon: Users, title: "5. Buying-Committee Stability", desc: "Departures, additions and role changes in the contact set. Each one moves close probability and most reps notice none of them in real time." },
              { icon: TrendingUp, title: "6. Momentum Signals", desc: "Pricing requests, security review starts, legal involvement, sentiment shifts. The leading indicators of close, not the lagging ones." },
            ].map((item, i) => (
              <div key={i} className="flex gap-4 p-6 bg-card rounded-xl border border-border">
                <item.icon className="h-8 w-8 text-primary flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-lg mb-1">{item.title}</h3>
                  <p className="text-muted-foreground">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Where most platforms stop */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-4">Where Most Platforms Stop</h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            Deal intelligence is widely claimed and narrowly delivered. Here is how to read what a vendor actually has.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-card p-8 rounded-xl border border-border">
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle className="h-6 w-6 text-amber-500" />
                <h3 className="text-xl font-bold">Surface-Level Deal Intelligence</h3>
              </div>
              <ul className="space-y-3 text-muted-foreground">
                <li>Stage and weighted amount with a confidence badge</li>
                <li>One or two signals (usually stage age, sometimes activity)</li>
                <li>Recomputed nightly, not continuously</li>
                <li>Depends on rep CRM hygiene</li>
                <li>Read-only insight, no execution</li>
              </ul>
            </div>
            <div className="bg-card p-8 rounded-xl border-2 border-primary/40">
              <div className="flex items-center gap-3 mb-4">
                <CheckCircle className="h-6 w-6 text-primary" />
                <h3 className="text-xl font-bold text-primary">Real Deal Intelligence</h3>
              </div>
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex gap-2"><CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" /> All six signals, computed from captured activity</li>
                <li className="flex gap-2"><CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" /> Recomputed continuously, not batched</li>
                <li className="flex gap-2"><CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" /> Independent of rep submissions</li>
                <li className="flex gap-2"><CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" /> Triggers workflows, not just notifications</li>
                <li className="flex gap-2"><CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" /> Auditable: every signal traceable to source events</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* NoQ5 Approach */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-4">How NoQ5 Computes Deal Intelligence</h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            Capture, Understand, Act. The same autonomous CRM loop, applied at deal level.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Eye, title: "1. Capture", desc: "Every email, meeting and call attached to the deal automatically. No rep logging required, no missing context." },
              { icon: Compass, title: "2. Understand", desc: "All six signals computed continuously, benchmarked against your historical closed-won cohort." },
              { icon: PlayCircle, title: "3. Act", desc: "Risk crosses threshold, follow-up workflow opens. Multi-thread drops below floor, briefing fires to the rep. CRM stays honest automatically." },
            ].map((item, i) => (
              <div key={i} className="bg-card p-6 rounded-xl border border-border">
                <item.icon className="h-10 w-10 text-primary mb-4" />
                <h3 className="font-bold text-lg mb-2">{item.title}</h3>
                <p className="text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Operationalizing */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-center mb-4">Operationalizing Deal Intelligence</h2>
          <p className="text-center text-muted-foreground mb-12">
            Three places deal intelligence pays back inside a quarter.
          </p>
          <div className="space-y-4">
            {[
              { title: "Pipeline reviews", desc: "Stop interrogating reps. Open the dashboard, sort by risk score, work the top 10 deals. 30-minute reviews instead of 90-minute ones." },
              { title: "Forecast calls", desc: "Run the AI call next to the rep call on every committed deal. The gap is the entire agenda." },
              { title: "Manager coaching", desc: "Signals at the deal level become coaching signals at the rep level. Latency-heavy reps get conversation skill work. Single-thread-heavy reps get multi-threading drills." },
            ].map((item, i) => (
              <div key={i} className="flex gap-4 p-5 bg-card rounded-xl border border-border">
                <Target className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-bold text-foreground mb-1">{item.title}</h3>
                  <p className="text-muted-foreground text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-12">Go Deeper</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "Revenue Intelligence", link: "/revenue-intelligence", desc: "The broader category deal intelligence sits inside." },
              { title: "Sales Forecasting Software", link: "/sales-forecasting-software", desc: "How deal-level signals roll up into the forecast." },
              { title: "Pipeline Management AI", link: "/pipeline-management-ai", desc: "Stalled deals, multi-thread coverage, stage advancement." },
              { title: "AI for CROs", link: "/ai-for-cros", desc: "What revenue leaders actually buy when they buy AI." },
              { title: "CRM Agent", link: "/crm-agent", desc: "The autonomous CRM execution layer in detail." },
              { title: "Command Center", link: "/product/command-center", desc: "Where deal intelligence surfaces for the CRO." },
            ].map((item, i) => (
              <Link key={i} to={item.link} className="bg-card p-6 rounded-xl border border-border hover:border-primary/50 transition-colors group">
                <h3 className="font-bold text-lg mb-2 group-hover:text-primary transition-colors">{item.title}</h3>
                <p className="text-muted-foreground text-sm mb-3">{item.desc}</p>
                <span className="text-primary text-sm font-medium flex items-center gap-1">Read <ArrowRight className="h-4 w-4" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">See Every Deal in Your Pipeline. Honestly.</h2>
          <p className="text-xl opacity-90 mb-8">
            A 30 minute working session with the founder. Bring your top 20 deals. Leave with the six signals on every one of them.
          </p>
          <Button size="lg" variant="secondary" asChild>
            <Link to="/trial?scrollTo=demo">Book a Demo <ArrowRight className="ml-2 h-5 w-5" /></Link>
          </Button>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-3xl font-bold text-center mb-12">Deal Intelligence: FAQ</h2>
          <div className="space-y-6">
            {[
              { q: "What is deal intelligence?", a: "The deal-level layer of revenue intelligence. Uses captured activity to compute objective signals on each opportunity: stage age, multi-thread, latency, momentum, sentiment, buying-committee changes." },
              { q: "Deal intelligence vs pipeline intelligence?", a: "Deal intelligence is computed at the single-opportunity level. Pipeline intelligence rolls deal-level signals up to coverage, velocity and risk concentration. You need both." },
              { q: "What signals does deal intelligence track?", a: "Six that predict close: activity density, multi-thread coverage, response latency, stage age vs cohort, buying-committee stability, momentum signals. Most platforms cover three or four. Autonomous CRM covers all six and recomputes them continuously." },
              { q: "How does deal intelligence improve forecast accuracy?", a: "By moving the forecast from a weighted roll-up of stage and amount to an evidence-based computation. Stalled late-stage deals get downgraded automatically. Rapidly multi-threading mid-stage deals get upgraded." },
              { q: "Does deal intelligence require rep input?", a: "It should not. The whole point is to compute deal health from captured activity so the answer is independent of what reps log. NoQ5 refuses to depend on rep submissions for any deal intelligence signal." },
            ].map((item, i) => (
              <div key={i} className="bg-card p-6 rounded-xl border border-border">
                <h3 className="font-bold text-lg mb-2">{item.q}</h3>
                <p className="text-muted-foreground">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default DealIntelligence;
